import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Pencil,
  ChartSpline,
  GitCompare,
  BookOpen,
  ThumbsUp,
  SendHorizontal,
  Loader2
} from 'lucide-react';
// import arrowDownLinear from '@/imgs/arrow-down-linear.svg';
import { useChatContext } from '@/contexts/ChatContext';
import useUserStore from '@/stores/userStore';
import DraggableChatButton from '@/components/DraggableChatButton';

// setActiveTab 是用于设置当前激活的 tab 的函数
const ChatSection: React.FC<{ scrollDown: () => void; setActiveTab: (tab: number) => void }> = ({
  // scrollDown,
  setActiveTab
}) => {
  const [inputValue, setInputValue] = useState('');
  // const [isChatOpen, setIsChatOpen] = useState(false);
  const { updateChatId } = useUserStore();
  const {
    selectedButton,
    isProcessing,
    setSelectedButton,
    processMessage,
    addMessage,
    clearMessages,
    isLoading
  } = useChatContext();

  const getButtonText = selectedButton => {
    const buttonTextMap = {
      advisory: '投顾文案',
      market: '市场解读',
      compare: '产品对比',
      interpretation: '产品解读',
      recommendation: '产品推荐'
    };
    // 找不到对应值时返回默认值
    return buttonTextMap[selectedButton] || '投顾文案';
  };

  const handleClick = (url: string, buttonType: string) => {
    console.log(url, buttonType, 'urlrllr');

    setSelectedButton(buttonType);
    // window.location.href = url;
    if (selectedButton !== buttonType) {
      updateChatId(null);
    }
  };

  const setSelectedButtonTextToDom = () => {
    if (editableRef.current) {
      const selectedButtonTextDom = document.querySelector('#selectedButtonText') as HTMLElement;
      if (!selectedButtonTextDom) {
        if (selectedButton === null || selectedButton === undefined) {
          return;
        }
        const before = document.createElement('span');
        before.className = 'text-sm font-extrabold text-colorPrimaryText';
        before.id = 'selectedButtonText';
        before.dataset.selectedButton = selectedButton;

        before.contentEditable = 'false';
        before.textContent = getButtonText(selectedButton);
        editableRef.current.insertBefore(before, editableRef.current.firstChild);

        const range = document.createRange();
        range.setStartAfter(before);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        if ((selectedButtonTextDom as HTMLElement).dataset.selectedButton !== selectedButton) {
          (selectedButtonTextDom as HTMLElement).textContent = getButtonText(selectedButton);
          (selectedButtonTextDom as HTMLElement).dataset.selectedButton = selectedButton;
        }
      }
    }
  };

  useEffect(() => {
    if (selectedButton) {
      setSelectedButtonTextToDom();
    }
  }, [selectedButton]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    const chatSendInputWrapper = document.querySelector('.chat-send-input-wrapper') as HTMLElement;
    if (chatSendInputWrapper) {
      chatSendInputWrapper.innerHTML = '';
    }

    const message = inputValue.trim();
    addMessage({
      position: 'right',
      content: message
    });
    setInputValue('');
    // initActive();
    setActiveTab(1);
    setSelectedButtonTextToDom();

    addMessage({
      position: 'left',
      content: message,
      loading: true
    });

    // 调用 Charts 组件中的接口处理逻辑
    await processMessage(message, selectedButton);
  };

  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      if (!e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        container.scrollBy({ left: e.deltaY, behavior: 'auto' });
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // const toggleChat = () => {
  //   setIsChatOpen(!isChatOpen);
  //   scrollDown();
  // };

  const initActive = () => {
    setActiveTab(1);
    setSelectedButton('advisory');
    setSelectedButtonTextToDom();
  };

  const handleNewChat = () => {
    clearMessages();
    setInputValue('');
    initActive();
  };

  const selectedButtonText = useMemo(() => getButtonText(selectedButton), [selectedButton]);

  const editableRef = useRef(null);

  const handleInputChange = () => {
    if (editableRef.current) {
      // 复制 editableRef 的 DOM
      const editableClone = editableRef.current.cloneNode(true) as HTMLElement;

      // 删除 clone 下 id 为 selectedButtonText 的 DOM
      const selectedButtonTextNode = editableClone.querySelector('#selectedButtonText');
      if (selectedButtonTextNode && selectedButtonTextNode.parentNode) {
        selectedButtonTextNode.parentNode.removeChild(selectedButtonTextNode);
      }
      console.log(selectedButtonTextNode, 'selectedButtonTextNode');

      if (!selectedButtonTextNode) {
        setSelectedButton(null);
      }

      // 获取处理后的文本内容
      const currentContent = editableClone.textContent || '';
      console.log(currentContent, 'text');
      setInputValue(currentContent);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 h-[200px]">
      {/* 聊天区域 */}
      <div className="relative h-full">
        <div className="relative h-8 w-full">
          <DraggableChatButton
            onClick={handleNewChat}
            initialPosition={{ x: window.innerWidth - 52, y: window.innerHeight - 200 }}
            boundarySelector="body"
          />
          {/* <div
            className="absolute left-[calc(50%-15px)] top-[calc(50%-15px)] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]"
            onClick={toggleChat}
          >
            <img src={arrowDownLinear} width={20} height={20} />
          </div> */}
        </div>

        <div className="absolute bottom-0 left-0 right-0 rounded-t-lg border-l border-[#F0F0F0] bg-white p-4 pt-2 shadow-[20px_-4px_20px_0_rgba(89,89,235,0.09)]">
          {/* 快捷功能按钮 */}
          <div className="my-4 flex justify-start gap-2 overflow-scroll pr-1" ref={containerRef}>
            <div
              className={`relative !rounded-md ${
                selectedButton === 'advisory' ? 'gradient-border-box' : 'border border-[#F0F0F0]'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing || isLoading}
                className="flex h-7 items-center gap-1 border-none px-2 py-1 text-xs text-[#595959] transition-all duration-200"
                onClick={() => {
                  handleClick('/aiAdvisorDiagnosis', 'advisory');
                }}
              >
                <Pencil className="h-4 w-4" />
                投顾文案
              </Button>
            </div>
            <div
              className={`relative !rounded-md ${
                selectedButton === 'market' ? 'gradient-border-box' : 'border border-[#F0F0F0]'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing || isLoading}
                className="flex h-7 items-center gap-1 border-none px-2 py-1 text-xs text-[#595959] transition-all duration-200"
                onClick={() => {
                  handleClick('/marketAnalysis', 'market');
                }}
              >
                <ChartSpline className="h-4 w-4" />
                市场解读
              </Button>
            </div>
            <div
              className={`relative !rounded-md ${
                selectedButton === 'compare' ? 'gradient-border-box' : 'border border-[#F0F0F0]'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing || isLoading}
                className="flex h-7 items-center gap-1 border-none px-2 py-1 text-xs text-[#595959] transition-all duration-200"
                onClick={() => {
                  handleClick('/productCompare', 'compare');
                }}
              >
                <GitCompare className="h-4 w-4" />
                产品对比
              </Button>
            </div>

            <div
              className={`relative !rounded-md ${
                selectedButton === 'interpretation'
                  ? 'gradient-border-box'
                  : 'border border-[#F0F0F0]'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing || isLoading}
                className="flex h-7 items-center gap-1 border-none px-2 py-1 text-xs text-[#595959] transition-all duration-200"
                onClick={() => {
                  handleClick('/productInterpretation', 'interpretation');
                }}
              >
                <BookOpen className="h-4 w-4" />
                产品解读
              </Button>
            </div>

            <div
              className={`relative !rounded-md ${
                selectedButton === 'recommendation'
                  ? 'gradient-border-box'
                  : 'border border-[#F0F0F0]'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing || isLoading}
                className="flex h-7 items-center gap-1 border-none px-2 py-1 text-xs text-[#595959] transition-all duration-200"
                onClick={() => {
                  handleClick('/productRecommendation', 'recommendation');
                }}
              >
                <ThumbsUp className="h-4 w-4" />
                产品推荐
              </Button>
            </div>
          </div>

          {/* 聊天输入框 */}
          <div className="relative mb-4 rounded-lg bg-white">
            {/* 渐变边框效果 */}
            <div className="outline-transparent">
              {isProcessing && <div className="chat-send-input-wrapper-disabled">处理中...</div>}
              <div className="gradient-border-box flex items-center justify-between gap-2.5 px-3 py-2">
                <div
                  ref={editableRef}
                  contentEditable={true}
                  suppressContentEditableWarning
                  onInput={handleInputChange}
                  onKeyDown={e => {
                    // 监测按下回车（Enter）
                    if (
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      !isProcessing &&
                      inputValue.trim() &&
                      !isLoading
                    ) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className={`chat-send-input-wrapper w-[calc(100%-24px)] overflow-hidden whitespace-pre-wrap text-left outline-transparent ${
                    !inputValue.trim() ? 'send-input-empty' : ''
                  } ${
                    selectedButton
                      ? `button-type-selected button-type-selected-${selectedButton}`
                      : ''
                  } ${isProcessing ? 'cursor-not-allowed opacity-0' : ''}`}
                >
                  <span
                    className="mr-1 text-sm font-extrabold text-colorPrimaryText"
                    contentEditable={false}
                    id="selectedButtonText"
                    data-selected-button={selectedButton}
                  >
                    {selectedButtonText}
                  </span>
                </div>

                {/* <Input
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && inputValue.trim() && !isProcessing) {
                      handleSendMessage();
                    }
                  }}
                  placeholder={isProcessing ? '处理中...' : '请帮我生成产品对比~'}
                  disabled={isProcessing || isLoading}
                  className={`flex-1 border-0 bg-transparent text-sm text-[#8C8C8C] placeholder:text-[#8C8C8C] focus-visible:ring-0 focus-visible:ring-offset-0 ${
                    isProcessing ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                /> */}
                <Button
                  size="sm"
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isProcessing || isLoading}
                  className={`h-6 w-6 min-w-6 rounded p-0 ${
                    inputValue.trim() && !isProcessing && !isLoading
                      ? 'bg-gradient-to-r from-[#3215F3] to-[#ECB1FF] hover:from-[#2A13E0] hover:to-[#D49AFF]'
                      : 'bg-[#D9D9D9] hover:bg-[#CCCCCC]'
                  }`}
                >
                  {isProcessing || isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  ) : (
                    <SendHorizontal className="h-4 w-4 text-white" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSection;
